import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import {
  Shield,
  ArrowLeft,
  Smartphone,
  KeyRound,
  CheckCircle2,
  Lock,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/auth.context";

/**
 * <M-018> Um código de reserva tem 8 caracteres hexadecimais; o do aplicativo tem 6
 * dígitos. A tela alterna entre os dois campos porque o `InputOTP` é fixo em 6 casas —
 * era ele, e não só o servidor, que impedia um código de reserva de ser digitado.
 */
const TAMANHO_DO_CODIGO_DE_RESERVA = 8;

const TwoFactorPage = () => {
  const navigate = useNavigate();
  const { verify2FA } = useAuth();
  const [code, setCode] = useState("");
  const [usandoCodigoDeReserva, setUsandoCodigoDeReserva] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const tamanhoEsperado = usandoCodigoDeReserva
    ? TAMANHO_DO_CODIGO_DE_RESERVA
    : 6;

  const trocarDeModo = () => {
    setUsandoCodigoDeReserva((anterior) => !anterior);
    setCode("");
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();

    if (code.length !== tamanhoEsperado) {
      toast({
        title: "Erro",
        description: usandoCodigoDeReserva
          ? "Por favor, digite o código de reserva de 8 caracteres"
          : "Por favor, digite o código de 6 dígitos",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      await verify2FA(code);
    } catch (error: unknown) {
      console.error("Erro na verificação 2FA:", error);
      setCode("");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    localStorage.removeItem("access_token");
    navigate("/login");
  };

  const features = [
    {
      icon: Shield,
      title: "Segurança Extra",
      description:
        "A autenticação de dois fatores protege sua conta de acessos não autorizados.",
    },
    {
      icon: Smartphone,
      title: "App Autenticador",
      description: "Use Google Authenticator, Authy ou outro app compatível.",
    },
    {
      icon: Lock,
      title: "Código Único",
      description:
        "O código é válido por apenas 30 segundos para máxima segurança.",
    },
  ];

  return (
    <div className="min-h-screen flex bg-white">
      {/* Left Section - Hero */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-12 flex-col justify-center relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
        </div>

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#2869C1]/20 to-[#9CCB3B]/10" />

        {/* Content */}
        <div className="relative z-10 space-y-10">
          {/* Logo + Title */}
          <div className="space-y-6">
            <img
              src="/logoSidebar.png"
              alt="Painel do E-Commerce"
              className="h-16"
            />
            <div className="space-y-4">
              <h1 className="text-4xl font-bold text-white leading-tight">
                Verificação de
                <br />
                <span className="text-[#9CCB3B]">Segurança</span>
              </h1>
              <p className="text-slate-400 text-lg max-w-md">
                A autenticação de dois fatores adiciona uma camada extra de
                proteção à sua conta.
              </p>
            </div>
          </div>

          {/* Features */}
          <div className="space-y-4">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex items-start gap-4 p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm"
              >
                <div className="p-2 rounded-lg bg-[#9CCB3B]/20">
                  <feature.icon className="h-5 w-5 text-[#9CCB3B]" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">{feature.title}</h3>
                  <p className="text-sm text-slate-400">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="text-slate-500 text-sm pt-8">
            © {new Date().getFullYear()} Painel do E-Commerce. Todos os direitos
            reservados.
          </div>
        </div>
      </div>

      {/* Right Section - 2FA Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md space-y-8">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <img
              src="/logoSidebar.png"
              alt="Painel do E-Commerce"
              className="h-12 mx-auto"
            />
          </div>

          {/* Header */}
          <div className="text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-[#2869C1]/10 flex items-center justify-center mb-6">
              <Shield className="h-8 w-8 text-[#2869C1]" />
            </div>
            <h2 className="text-3xl font-bold text-slate-900">
              Autenticação 2FA
            </h2>
            <p className="mt-2 text-slate-600">
              {usandoCodigoDeReserva
                ? "Digite um dos códigos de reserva que você guardou ao ativar o 2FA"
                : "Digite o código de 6 dígitos do seu aplicativo autenticador"}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleVerify} className="space-y-6">
            {usandoCodigoDeReserva ? (
              <div className="space-y-2">
                <Label htmlFor="backup_code">Código de reserva</Label>
                <Input
                  id="backup_code"
                  autoFocus
                  autoComplete="one-time-code"
                  spellCheck={false}
                  maxLength={TAMANHO_DO_CODIGO_DE_RESERVA}
                  placeholder="A1B2C3D4"
                  className="h-14 text-center text-lg font-mono tracking-[0.3em] uppercase bg-white border-slate-200"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  disabled={isLoading}
                />
                <p className="text-xs text-slate-500">
                  Cada código só pode ser usado uma vez.
                </p>
              </div>
            ) : (
            <div className="flex justify-center">
              <InputOTP
                maxLength={6}
                value={code}
                onChange={(value) => setCode(value)}
                disabled={isLoading}
              >
                <InputOTPGroup className="gap-2">
                  <InputOTPSlot
                    index={0}
                    className="w-12 h-14 text-lg border-slate-200 bg-white rounded-lg"
                  />
                  <InputOTPSlot
                    index={1}
                    className="w-12 h-14 text-lg border-slate-200 bg-white rounded-lg"
                  />
                  <InputOTPSlot
                    index={2}
                    className="w-12 h-14 text-lg border-slate-200 bg-white rounded-lg"
                  />
                  <InputOTPSlot
                    index={3}
                    className="w-12 h-14 text-lg border-slate-200 bg-white rounded-lg"
                  />
                  <InputOTPSlot
                    index={4}
                    className="w-12 h-14 text-lg border-slate-200 bg-white rounded-lg"
                  />
                  <InputOTPSlot
                    index={5}
                    className="w-12 h-14 text-lg border-slate-200 bg-white rounded-lg"
                  />
                  </InputOTPGroup>
              </InputOTP>
            </div>
            )}

            <div className="space-y-3">
              <Button
                type="submit"
                className="w-full h-12 text-base font-semibold bg-[#2869C1] hover:bg-[#1d5299]"
                disabled={code.length !== tamanhoEsperado || isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Verificando...
                  </div>
                ) : (
                  "Verificar Código"
                )}
              </Button>

              <Button
                type="button"
                variant="outline"
                className="w-full h-12 border-slate-200 text-slate-700 hover:bg-slate-50"
                onClick={handleBack}
                disabled={isLoading}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar ao Login
              </Button>
            </div>
          </form>

          {/* Help Text */}
          <div className="text-center space-y-4">
            {/*
              <M-018> Este bloco era um botão "Reenviar código" sem `onClick`, que não
              reenviava nada — não há código a enviar num TOTP. Ele virou a saída que
              faltava: quem perdeu o aparelho entra com um dos oito códigos de reserva.
            */}
            <div className="text-sm text-slate-500">
              <p>
                {usandoCodigoDeReserva
                  ? "Está com o aplicativo autenticador em mãos?"
                  : "Perdeu o acesso ao aplicativo autenticador?"}
              </p>
              <button
                type="button"
                className="text-[#2869C1] hover:underline inline-flex items-center gap-1 mt-1"
                onClick={trocarDeModo}
                disabled={isLoading}
              >
                <KeyRound className="h-3 w-3" />
                {usandoCodigoDeReserva
                  ? "Usar o código do aplicativo"
                  : "Usar um código de reserva"}
              </button>
            </div>

            <div className="text-xs text-slate-400 p-4 bg-slate-100 rounded-lg">
              <p>
                {usandoCodigoDeReserva
                  ? "Os códigos de reserva são os oito que você guardou ao ativar o 2FA. Depois de entrar, gere novos em Minha Conta."
                  : "Use um aplicativo como Google Authenticator ou Authy para gerar o código de verificação."}
              </p>
            </div>
          </div>

          {/* Trust Badges */}
          <div className="flex items-center justify-center gap-6 pt-4 text-sm text-slate-500">
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              Conexão segura
            </div>
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              Dados criptografados
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorPage;
